import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Sparkles, CalendarIcon, Calculator as CalcIcon, 
  ArrowRight, Loader2, RefreshCw, Star, Heart, User, Share2, Copy, Check, Sun, Cake
} from 'lucide-react';
import { Footer } from '../components/Footer';
import SEO, { pageSEO } from '../components/SEO';

// Floating Hearts Component
const FloatingHearts = () => (
  <div className="floating-hearts">
    {[...Array(10)].map((_, i) => (
      <div key={i} className="floating-heart">
        <Heart className="w-6 h-6 fill-current" />
      </div>
    ))}
  </div>
);
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Share Component
const ShareResults = ({ person1Name, person2Name, compatibility }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = window.location.origin + '/calculator';
  const shareText = `✨ ${person1Name} & ${person2Name} are ${compatibility}% cosmically compatible! 🔮 Discover YOUR numerology match with Karine Burt, Psychic Medium & Numerology Matchmaker!`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
    window.open(url, '_blank');
  };
  
  const shareViaEmail = () => {
    const subject = `✨ Our Numerology Compatibility: ${compatibility}%!`;
    const body = `${shareText}\n\nCheck it out: ${shareUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="ethereal-card rounded-2xl p-6 mt-8">
      <div className="flex items-center gap-3 mb-6">
        <Share2 className="w-6 h-6 text-[#8B5CF6]" />
        <h3 className="font-mystical text-xl text-[#2D1B3D] font-bold">Share Your Results!</h3>
      </div>
      <p className="text-[#4A3A5A] mb-6">Let your friends discover their cosmic compatibility too! ✨</p>
      
      <div className="flex flex-wrap gap-3">
        {/* Facebook */}
        <button
          onClick={shareToFacebook}
          className="inline-flex items-center gap-2 bg-[#1877F2] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
          data-testid="share-facebook"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Facebook
        </button>
        
        {/* Twitter/X */}
        <button
          onClick={shareToTwitter}
          className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
          data-testid="share-twitter"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          X
        </button>
        
        {/* WhatsApp */}
        <button
          onClick={shareToWhatsApp}
          className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
          data-testid="share-whatsapp"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </button>
        
        {/* Email */}
        <button
          onClick={shareViaEmail}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
          data-testid="share-email"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email
        </button>
        
        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white px-5 py-3 rounded-full font-bold hover:shadow-lg hover:scale-105 transition-all"
          data-testid="share-copy"
        >
          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
};

const NumerologyDisplay = ({ title, number, icon: Icon }) => (
  <div className="text-center">
    <div className="w-8 h-8 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-purple-400/50 to-amber-400/50 flex items-center justify-center mx-auto mb-1">
      <Icon className="w-4 h-4 md:w-5 md:h-5 text-purple-700" />
    </div>
    <div className="number-circle w-10 h-10 md:w-14 md:h-14 text-base md:text-xl mx-auto mb-1">
      {number}
    </div>
    <p className="text-[#3D2A4D] text-xs md:text-sm font-semibold">{title}</p>
  </div>
);

export const CalculatorPage = () => {
  const [person1, setPerson1] = useState({ name: '', birth_date: null });
  const [person2, setPerson2] = useState({ name: '', birth_date: null });
  const [calendar1Open, setCalendar1Open] = useState(false);
  const [calendar2Open, setCalendar2Open] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(null);
  const [loadingReading, setLoadingReading] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!person1.name || !person1.birth_date || !person2.name || !person2.birth_date) {
      return;
    }
    setLoading(true);
    setReading(null);
    try {
      const response = await axios.post(`${API}/calculator`, {
        person1_name: person1.name,
        person1_birth_date: format(person1.birth_date, 'yyyy-MM-dd'),
        person2_name: person2.name,
        person2_birth_date: format(person2.birth_date, 'yyyy-MM-dd')
      });
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReading = async () => {
    if (!result) return;
    setLoadingReading(true);
    try {
      const response = await axios.post(`${API}/reading`, {
        person1_name: person1.name,
        person1_birth_date: format(person1.birth_date, 'yyyy-MM-dd'),
        person2_name: person2.name,
        person2_birth_date: format(person2.birth_date, 'yyyy-MM-dd')
      });
      setReading(response.data.reading);
    } catch (error) {
      console.error('Error generating reading:', error);
    } finally {
      setLoadingReading(false);
    }
  };

  const radarData = result ? [
    { subject: 'Life Path', A: result.compatibility.life_path, fullMark: 100 },
    { subject: 'Expression', A: result.compatibility.expression, fullMark: 100 },
    { subject: 'Soul Urge', A: result.compatibility.soul_urge, fullMark: 100 },
    { subject: 'Personality', A: result.compatibility.personality, fullMark: 100 },
    { subject: 'Attitude', A: result.compatibility.attitude, fullMark: 100 },
    { subject: 'Birthday', A: result.compatibility.birthday, fullMark: 100 },
  ] : [];

  return (
    <div className="min-h-screen swirl-bg">
      <SEO {...pageSEO.calculator} />
      <FloatingHearts />
      
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-purple-200/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3" data-testid="calc-logo">
              <motion.div 
                className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles className="w-6 h-6 text-white" />
              </motion.div>
              <span className="font-mystical text-3xl text-magic-gradient font-bold">Intuitive Matchmaker</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link 
                to="/login"
                className="text-[#3D2A4D] hover:text-[#8B5CF6] transition-colors font-semibold text-lg"
                data-testid="calc-nav-login"
              >
                Sign In
              </Link>
              <Link to="/register" data-testid="calc-nav-register">
                <Button className="rounded-full btn-magic px-6 py-3 font-bold">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 via-amber-400 to-pink-500 mb-6 glow-purple">
            <CalcIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-mystical text-5xl md:text-6xl text-[#2D1B3D] font-bold mb-4">
            Soulmate Calculator
          </h1>
          <p className="font-whimsy text-[#4A3A5A] text-2xl md:text-3xl font-bold max-w-xl mx-auto">
            Do you have a special someone in your life and want to learn if you're both compatible?
          </p>
        </motion.div>

        {/* Calculator Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleCalculate}
          className="ethereal-card rounded-3xl p-8 md:p-10 mb-8"
        >
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Person 1 */}
            <div className="space-y-4">
              <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-4">First Person</h3>
              <div className="space-y-2">
                <Label className="text-[#3D2A4D] font-semibold text-lg">Full Birth Name</Label>
                <Input
                  value={person1.name}
                  onChange={(e) => setPerson1(p => ({ ...p, name: e.target.value }))}
                  placeholder="Enter full name"
                  className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C]"
                  data-testid="person1-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3D2A4D] font-semibold text-lg">Birth Date</Label>
                <Popover open={calendar1Open} onOpenChange={setCalendar1Open}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-14 justify-start text-left font-medium bg-white/70 border-purple-200 hover:border-[#8B5CF6] rounded-xl text-lg"
                      data-testid="person1-birthdate-trigger"
                    >
                      <CalendarIcon className="mr-3 h-5 w-5 text-[#8B5CF6]" />
                      {person1.birth_date ? (
                        <span className="text-[#2D1B3D]">{format(person1.birth_date, 'MMMM d, yyyy')}</span>
                      ) : (
                        <span className="text-[#8C7A9C]">Select birth date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-purple-200" align="start">
                    <Calendar
                      mode="single"
                      selected={person1.birth_date}
                      onSelect={(date) => {
                        setPerson1(p => ({ ...p, birth_date: date }));
                        setCalendar1Open(false);
                      }}
                      disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                      captionLayout="dropdown-buttons"
                      fromYear={1920}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Person 2 */}
            <div className="space-y-4">
              <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold mb-4">Second Person</h3>
              <div className="space-y-2">
                <Label className="text-[#3D2A4D] font-semibold text-lg">Full Birth Name</Label>
                <Input
                  value={person2.name}
                  onChange={(e) => setPerson2(p => ({ ...p, name: e.target.value }))}
                  placeholder="Enter full name"
                  className="bg-white/70 border-purple-200 focus:border-[#8B5CF6] rounded-xl h-14 text-[#2D1B3D] text-lg font-medium placeholder:text-[#8C7A9C]"
                  data-testid="person2-name-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3D2A4D] font-semibold text-lg">Birth Date</Label>
                <Popover open={calendar2Open} onOpenChange={setCalendar2Open}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-14 justify-start text-left font-medium bg-white/70 border-purple-200 hover:border-[#8B5CF6] rounded-xl text-lg"
                      data-testid="person2-birthdate-trigger"
                    >
                      <CalendarIcon className="mr-3 h-5 w-5 text-[#8B5CF6]" />
                      {person2.birth_date ? (
                        <span className="text-[#2D1B3D]">{format(person2.birth_date, 'MMMM d, yyyy')}</span>
                      ) : (
                        <span className="text-[#8C7A9C]">Select birth date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-purple-200" align="start">
                    <Calendar
                      mode="single"
                      selected={person2.birth_date}
                      onSelect={(date) => {
                        setPerson2(p => ({ ...p, birth_date: date }));
                        setCalendar2Open(false);
                      }}
                      disabled={(date) => date > new Date() || date < new Date('1920-01-01')}
                      captionLayout="dropdown-buttons"
                      fromYear={1920}
                      toYear={new Date().getFullYear()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Button
              type="submit"
              disabled={loading || !person1.name || !person1.birth_date || !person2.name || !person2.birth_date}
              className="rounded-full btn-magic px-14 py-7 text-xl font-bold"
              data-testid="calculate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  Calculate Compatibility
                  <ArrowRight className="ml-3 w-6 h-6" />
                </>
              )}
            </Button>
          </div>
        </motion.form>

        {/* Results */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Overall Score */}
            <div className="ethereal-card rounded-3xl p-10 text-center">
              <div className="text-7xl md:text-8xl font-mystical text-magic-gradient mb-4 font-bold">
                {result.combined_overall || result.compatibility.overall}%
              </div>
              <p className="text-[#3D2A4D] text-2xl font-semibold">Overall Cosmic Compatibility</p>
              <p className="text-[#6D5A7D] text-sm mt-2">Combining Pythagorean Numerology & Chinese Zodiac</p>
            </div>

            {/* Chinese Zodiac Section */}
            {result.person1.chinese_zodiac && result.person2.chinese_zodiac && (
              <div className="ethereal-card rounded-2xl p-8">
                <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-6 text-center font-bold">🐉 Chinese Zodiac 🐉</h3>
                <div className="grid md:grid-cols-3 gap-6 items-center">
                  {/* Person 1 Zodiac */}
                  <div className="text-center">
                    <div className="text-6xl mb-3">{result.person1.chinese_zodiac.emoji}</div>
                    <p className="text-[#8B5CF6] text-lg font-bold">{result.person1.name}</p>
                    <p className="font-mystical text-2xl text-[#2D1B3D] font-bold">{result.person1.chinese_zodiac.animal}</p>
                    <p className="text-[#6D5A7D] text-sm mt-2">{result.person1.chinese_zodiac.traits}</p>
                  </div>
                  
                  {/* Zodiac Compatibility */}
                  <div className="text-center bg-gradient-to-br from-purple-100 to-amber-100 rounded-2xl p-6">
                    <div className="text-4xl font-mystical text-magic-gradient font-bold mb-2">
                      {result.zodiac_compatibility.score}%
                    </div>
                    <p className="text-[#2D1B3D] font-bold text-lg">{result.zodiac_compatibility.level}</p>
                    <p className="text-[#6D5A7D] text-sm mt-2">{result.zodiac_compatibility.description}</p>
                  </div>
                  
                  {/* Person 2 Zodiac */}
                  <div className="text-center">
                    <div className="text-6xl mb-3">{result.person2.chinese_zodiac.emoji}</div>
                    <p className="text-[#D4AF37] text-lg font-bold">{result.person2.name}</p>
                    <p className="font-mystical text-2xl text-[#2D1B3D] font-bold">{result.person2.chinese_zodiac.animal}</p>
                    <p className="text-[#6D5A7D] text-sm mt-2">{result.person2.chinese_zodiac.traits}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Numerology Numbers */}
              <div className="ethereal-card rounded-2xl p-4 md:p-8">
                <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-6 text-center font-bold">Numerology Profiles</h3>
                
                {/* Person 1 */}
                <div className="mb-8">
                  <p className="text-[#8B5CF6] text-lg font-bold text-center mb-4">{result.person1.name}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <NumerologyDisplay title="Life Path" number={result.person1.numerology.life_path} icon={Star} />
                    <NumerologyDisplay title="Expression" number={result.person1.numerology.expression} icon={Sparkles} />
                    <NumerologyDisplay title="Soul Urge" number={result.person1.numerology.soul_urge} icon={Heart} />
                    <NumerologyDisplay title="Personality" number={result.person1.numerology.personality} icon={User} />
                    <NumerologyDisplay title="Attitude" number={result.person1.numerology.attitude} icon={Sun} />
                    <NumerologyDisplay title="Birthday" number={result.person1.numerology.birthday} icon={Cake} />
                  </div>
                </div>
                
                {/* Divider */}
                <div className="border-t border-purple-200 my-6"></div>
                
                {/* Person 2 */}
                <div>
                  <p className="text-[#D4AF37] text-lg font-bold text-center mb-4">{result.person2.name}</p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <NumerologyDisplay title="Life Path" number={result.person2.numerology.life_path} icon={Star} />
                    <NumerologyDisplay title="Expression" number={result.person2.numerology.expression} icon={Sparkles} />
                    <NumerologyDisplay title="Soul Urge" number={result.person2.numerology.soul_urge} icon={Heart} />
                    <NumerologyDisplay title="Personality" number={result.person2.numerology.personality} icon={User} />
                    <NumerologyDisplay title="Attitude" number={result.person2.numerology.attitude} icon={Sun} />
                    <NumerologyDisplay title="Birthday" number={result.person2.numerology.birthday} icon={Cake} />
                  </div>
                </div>
              </div>

              {/* Radar Chart */}
              <div className="ethereal-card rounded-2xl p-8">
                <h3 className="font-mystical text-2xl text-[#2D1B3D] mb-8 text-center font-bold">Compatibility Chart</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#C4B5FD" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: '#3D2A4D', fontSize: 13, fontWeight: 600 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#6D5A7D', fontSize: 11 }} />
                      <Radar name="Compatibility" dataKey="A" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.4} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* AI Reading */}
            <div className="ethereal-card rounded-2xl p-10">
              <div className="flex items-center justify-between mb-8">
                <h3 className="font-mystical text-2xl text-[#2D1B3D] font-bold">✨ AI Cosmic Reading ✨</h3>
                <Button
                  onClick={generateReading}
                  disabled={loadingReading}
                  className="rounded-full btn-magic font-bold px-6"
                  data-testid="calc-generate-reading-btn"
                >
                  {loadingReading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Channeling...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" />
                      {reading ? 'New Reading' : 'Generate Reading'}
                    </>
                  )}
                </Button>
              </div>

              {reading ? (
                <div className="text-[#2D1B3D] font-medium leading-loose whitespace-pre-line text-xl">
                  {reading}
                </div>
              ) : (
                <div className="text-center py-10">
                  <Sparkles className="w-16 h-16 text-[#8B5CF6]/40 mx-auto mb-4" />
                  <p className="text-[#4A3A5A] text-lg font-medium">
                    Click "Generate Reading" to receive a personalized cosmic compatibility reading
                  </p>
                </div>
              )}
            </div>

            {/* Share Results */}
            <ShareResults 
              person1Name={result.person1.name} 
              person2Name={result.person2.name} 
              compatibility={result.compatibility.overall} 
            />

            {/* CTA */}
            <div className="text-center py-10">
              <p className="text-[#3D2A4D] text-xl font-medium mb-6">Want to find more compatible souls?</p>
              <Link to="/register" data-testid="calc-cta-register">
                <Button className="rounded-full btn-magic px-10 py-6 text-xl font-bold">
                  Create Your Profile
                  <ArrowRight className="ml-3 w-6 h-6" />
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        <Footer />
      </main>
    </div>
  );
};

export default CalculatorPage;
